import React, { memo } from "react";

import { ShowcaseCard, ShowcaseNotice } from "ui/components/Showcase";
import CheckIcon from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleEditCard/components/VehicleWizardIntro/scenes/CheckIcon";

import { getContactName, getInitials, getPictureUrls, isCompanyContact } from "../helpers";
import { ContactWizardSceneProps } from "../types";

/** Korak "Pregled": što je od podataka kontakta upisano i koliko je kontakt potpun. */
const SummaryScene: React.FC<ContactWizardSceneProps> = ({ formValues }) => {
  const isCompany = isCompanyContact(formValues);
  const name = getContactName(formValues);
  const hasName = isCompany ? !!formValues.companyName : !!(formValues.firstName || formValues.lastName);

  const rows = [
    { label: isCompany ? "Naziv tvrtke" : "Ime i prezime", done: hasName },
    { label: "OIB", done: !!formValues.personalIdentificationNumber },
    { label: "Grupe", done: (formValues.contactGroupIds?.length ?? 0) > 0 },
    { label: "Slike", done: getPictureUrls(formValues).length > 0 },
  ];
  const doneCount = rows.filter((row) => row.done).length;
  const percent = Math.round((doneCount / rows.length) * 100);

  return (
    <>
      <ShowcaseCard className="left-[0.5rem] top-[-1rem] w-[17rem] h-[18rem]" delay={0}>
        <div className="p-5">
          <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Pregled kontakta</div>
          <div className="mt-2 flex items-center gap-2">
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-[0.65rem] font-bold text-white flex-shrink-0">
              {getInitials(name)}
            </span>
            <div className="text-sm font-bold text-slate-800 truncate">{name}</div>
          </div>
          <div className="mt-3 space-y-2">
            {rows.map((row) => (
              <div key={row.label} className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-3 py-2 text-[0.7rem]">
                <span className={row.done ? "font-medium text-slate-700" : "text-slate-400"}>{row.label}</span>
                {row.done ? <CheckIcon /> : <span className="w-5 h-5 rounded-full border-2 border-slate-200 flex-shrink-0" />}
              </div>
            ))}
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="right-[-0.5rem] top-[6rem] w-[14rem]" delay={0.25}>
        <div className="p-5">
          <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Potpunost</div>
          <div className="mt-2 text-xl font-bold text-slate-800">{percent}%</div>
          <div className="mt-2 h-1.5 rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400" style={{ width: `${percent}%` }} />
          </div>
          <div className="mt-2 text-[0.65rem] text-slate-400">
            {doneCount} od {rows.length} podataka upisano
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="left-[-1.5rem] bottom-[-0.5rem] w-[15rem]" delay={0.5}>
        <ShowcaseNotice
          title="Završite jednim klikom"
          subtitle="Kontakt je spreman za rad"
          iconClassName="bg-emerald-100"
          icon={
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#047857" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M22 11.08V12a10 10 0 11-5.93-9.14M22 4L12 14.01l-3-3" />
            </svg>
          }
        />
      </ShowcaseCard>
    </>
  );
};

export default memo(SummaryScene);
